(function () {
    'use strict';

    class ListFilter {
        #filters = [];
        #filterElements = new Map();

        constructor(options) {
            this.element = options.element;
            this.controlsContainer = options.controlsContainer || document;
            this.excludeClass =
                options.excludeFromFilteringClass || 'filterList__exclude';
            this.hiddenClass = options.hiddenClass;
            this.lastClass = options.lastClass || 'last';
            this.urlIsUpdatable = options.urlIsUpdatable || false;
            this.filterNames = [];
            this.initCallback = options.initCallback;
            this.filtersCallback = options.filtersCallback;

            if (this.element && this.element.hasAttribute('data-filter-names')) {
                const attr = this.element.getAttribute('data-filter-names');

                if (attr && attr.length > 0) {
                    this.filterNames = attr.split(' ');
                } else {
                    console.warn('data-filter-names has no value');
                    return;
                }
            } else {
                console.warn(
                    'Element not found or has no data-filter-names attribute!'
                );
                return;
            }

            this.setDefaultFilters(this.filterNames);
            this.setEventHandlers();
            this.updateFiltersfromURL();
            this.updateBrowserHistory();

            if (typeof this.initCallback === 'function') {
                this.initCallback();
            }

            this.applyFilters(this.element);
        }

        updateBrowserHistory() {
            if (this.urlIsUpdatable) {
                history.replaceState(
                    { filters: this.#filters.map((filter) => ({ ...filter })) },
                    document.title,
                    window.location.href
                );
            }
        }

        setDefaultFilters(filterNames) {
            filterNames.forEach((filterName) => {
                this.#filterElements.set(
                    filterName,
                    this.controlsContainer.querySelector(`[name="${CSS.escape(filterName)}"]`)
                );
                const ignoreValue = this.getFilterIgnoreValue(filterName);
                const filterValue = this.getFilterValue(filterName);

                this.#filters.push({
                    name: filterName,
                    value: filterValue,
                    ignoreValue: ignoreValue
                });
            });
        }

        getFilterIgnoreValue(filterName) {
            const filterElement = this.#filterElements.get(filterName);

            let value;

            if (filterElement) {
                const dataAttr = filterElement.getAttribute('data-ignore');

                if (filterElement.tagName === 'SELECT') {
                    value = dataAttr || undefined;
                }
            } else {
                console.warn(
                    'No filter with name ' + filterName + ' was found in markup!'
                );
            }

            return value;
        }

        getFilterValue(filterName) {
            const filterElement = this.#filterElements.get(filterName);

            let value;

            if (filterElement) {
                if (filterElement.getAttribute('type') === 'checkbox') {
                    value = filterElement.checked
                        ? filterElement.value
                        : undefined;
                } else {
                    value = filterElement.value;
                }
            } else {
                console.warn(
                    'No filter with name ' + filterName + ' was found in markup!'
                );
            }

            return value;
        }

        isValidFilterValue(filterName, value) {
            const element = this.#filterElements.get(filterName);
            return element?.tagName !== 'SELECT' ||
                [...element.options].some((option) => option.value === value);
        }

        updateFiltersfromURL() {
            this.filterNames.forEach((filterName) => {
                const searchParams = new URLSearchParams(
                    window.location.search
                );

                const newValue = searchParams.get(filterName);

                if (newValue && this.isValidFilterValue(filterName, newValue)) {
                    this.updateFilters({
                        name: filterName,
                        value: newValue
                    });

                    this.updateDOM(this.#filters);
                }
            });
        }

        updateFilters(updatedFilter) {
            const filter = this.#filters.find(
                (filter) => filter.name === updatedFilter.name
            );

            if (filter && this.isValidFilterValue(filter.name, updatedFilter.value) && filter.value !== updatedFilter.value) {
                filter.value = updatedFilter.value;
                this.applyFilters(this.element);
            }
        }

        setEventHandlers() {
            this.filterNames.forEach((filterName) => {
                const filterElement = this.#filterElements.get(filterName);

                if (filterElement) {
                    filterElement.addEventListener('change', () => {
                        this.updateFilters({
                            name: filterName,
                            value: this.getFilterValue(filterName)
                        });

                        if (this.urlIsUpdatable) {
                            const url = this.updateURL(
                                new URL(window.location.href),
                                this.#filters
                            );

                            history.pushState(
                                { filters: this.#filters.map((filter) => ({ ...filter })) },
                                document.title,
                                url
                            );
                        }
                    });
                }
            });

            if (this.urlIsUpdatable) {
                window.addEventListener('popstate', (e) => {
                    if (e.state && e.state.filters) {
                        e.state.filters.forEach((savedFilter) => {
                            const filter = this.#filters.find((item) => item.name === savedFilter.name);
                            if (filter && this.isValidFilterValue(filter.name, savedFilter.value)) {
                                filter.value = savedFilter.value;
                            }
                        });

                        this.updateDOM(this.#filters);
                        this.applyFilters(this.element);
                    }
                });
            }
        }

        updateDOM(filters) {
            filters.forEach((filter) => {
                const element = this.#filterElements.get(filter.name);

                if (element) {
                    if (element.tagName === 'SELECT') {
                        element.value = filter.value;
                    } else if (element.tagName === 'INPUT') {
                        if (element.getAttribute('type') === 'checkbox') {
                            element.checked = element.value === filter.value;
                        }
                    }
                }
            });
        }

        // Public method for changing filters via other scripts
        setFilters(filters) {
            for (const property in filters) {
                this.#filters.forEach((item) => {
                    if (item.name === property && this.isValidFilterValue(item.name, filters[property])) {
                        item.value = filters[property];
                    }
                });
            }

            this.updateDOM(this.#filters);
            this.applyFilters(this.element);

            if (this.urlIsUpdatable) {
                const url = this.updateURL(
                    new URL(window.location.href),
                    this.#filters
                );

                history.pushState(
                    { filters: this.#filters.map((filter) => ({ ...filter })) },
                    document.title,
                    url
                );
            }
        }

        // Returns elements that match all applicable filters
        getMatchedItems(listItems) {
            const matchedItems = [];

            const applicableFilters = this.#filters.filter((item) => {
                return (
                    item.value !== undefined &&
                    item.value !== item.ignoreValue
                );
            });

            listItems.forEach((element) => {
                let matched = true;

                applicableFilters.forEach((filter) => {
                    const hasThisFilter = element.hasAttribute(
                        'data-filter-' + filter.name
                    );

                    const filterValue = element.getAttribute(
                        'data-filter-' + filter.name
                    );

                    const elementMatchesFilter =
                        filterValue && filter.value
                            ? filterValue
                                .split(' ')
                                .includes(filter.value)
                            : false;

                    if (!hasThisFilter || !elementMatchesFilter) {
                        matched = false;
                    }
                });

                if (matched) {
                    matchedItems.push(element);
                }
            });

            return matchedItems;
        }

        applyFilters(element) {
            const listItems = [...element.children].filter((item) => {
                return !item.classList.contains(this.excludeClass);
            });

            const matchedItems = this.getMatchedItems(listItems);

            // Hide all items
            listItems.forEach((el) => {
                if (this.hiddenClass) {
                    el.classList.add(this.hiddenClass);
                } else {
                    el.style.display = 'none';
                }
            });

            // Unhide matched items
            if (matchedItems.length === 0) {
                element.classList.add('is-empty');
            } else {
                matchedItems.forEach((el) => {
                    if (this.hiddenClass) {
                        el.classList.remove(this.hiddenClass);
                    } else {
                        el.style.display = '';
                    }
                });

                element.classList.remove('is-empty');
            }

            // Add a class to the last visible item
            if (this.lastClass) {
                const lastVisibleElement = element.querySelector(
                    `.${this.lastClass}`
                );

                if (lastVisibleElement) {
                    lastVisibleElement.classList.remove(this.lastClass);
                }

                if (matchedItems.length > 0) {
                    matchedItems[matchedItems.length - 1].classList.add(
                        this.lastClass
                    );
                }
            }

            if (typeof this.filtersCallback === 'function') {
                this.filtersCallback(matchedItems);
            }
        }

        updateURL(url, filters) {
            filters.forEach((filter) => {
                if (
                    filter.value === undefined ||
                    filter.value.length === 0 ||
                    filter.value === filter.ignoreValue
                ) {
                    url.searchParams.delete(filter.name);
                } else {
                    url.searchParams.set(filter.name, filter.value);
                }
            });

            return url;
        }
    }

    window.QLD = window.QLD || {};
    QLD.ListFilter = ListFilter;
}());
